import React, { useEffect, useState } from "react";
import {
  VStack,
  HStack,
  Text,
  Box,
  Stack,
  IconButton,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import KYDCheckbox from "src/ui/KYDCheckbox";
import KYDInput from "src/ui/KYDInput";
import KYDButton from "src/ui/KYDButton";
import KYDSearchInput from "src/ui/KYDSearchInput";
import { Description } from "./Section";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import {
  InstantSearch,
  useInfiniteHits,
  useSearchBox,
} from "react-instantsearch-core";
import { algoliasearch } from "algoliasearch";
import { useForm } from "react-hook-form";
import actions from "src/actions";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import { FiPlus } from "react-icons/fi";

interface SendTestEmailProps {
  organizationUsers: KYDOrganizationUser[];
  campaignId: string;
}

const SendTestEmailComponent = ({
  organizationUsers,
  campaignId,
}: SendTestEmailProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const form = useForm<{
    email: string;
  }>({
    defaultValues: {
      email: "",
    },
  });
  const { items } = useInfiniteHits();
  const { refine } = useSearchBox();

  const [selectedTestEmails, setSelectedTestEmails] = useState<string[]>([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const canSendTestEmail =
    selectedTestEmails.length > 0 && selectedContact && !isLoading;

  const onAddTestEmail = (text: string) => {
    if (text) {
      const value = text.trim();
      if (!selectedTestEmails.includes(value)) {
        setSelectedTestEmails([...selectedTestEmails, value]);
        form.setValue("email", "");
      }
    }
  };
  const onChangeTestEmails = (email: string) => {
    if (selectedTestEmails.includes(email)) {
      setSelectedTestEmails(selectedTestEmails.filter((e) => e !== email));
    } else {
      setSelectedTestEmails([...selectedTestEmails, email]);
    }
  };

  const onSendTestEmail = async () => {
    setIsLoading(true);
    try {
      await actions.ADMIN.createCampaignTest(campaignId, {
        recipients: selectedTestEmails,
        contact_id: selectedContact?.id,
      });
      toast({
        title: "Test email sent",
        description:
          "The test email has been sent to the selected email addresses",
        status: "success",
      });
    } catch (error) {
      console.error("Error sending test email:", error);
      toast({
        title: "Error sending test email",
        description: "Please try again",
        status: "error",
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (items.length > 0 && !selectedContact) {
      const randomContact = items[Math.floor(Math.random() * items.length)];
      setSelectedContact({
        id: randomContact.id,
        email: randomContact.email,
      });
    }
  }, [items]);

  return (
    <Stack width={"full"}>
      <Text
        color="#1A202C"
        fontSize="24px"
        fontStyle="normal"
        fontWeight="600"
        letterSpacing="-0.72px"
      >
        Send Test Email
      </Text>
      <VStack width={"full"} spacing={2} align="flex-start">
        {organizationUsers.map((user) => (
          <HStack key={user.id} spacing={2}>
            <KYDCheckbox
              key={user.id}
              value={user.email}
              onChange={() => onChangeTestEmails(user.email)}
              isChecked={selectedTestEmails.includes(user.email)}
            />
            <Text
              fontSize="14px"
              color="#00000099"
              fontFamily="Helvetica"
              as="label"
              htmlFor={`email-${user.email}`}
            >
              {user.email}
            </Text>
          </HStack>
        ))}
      </VStack>
      <HStack align="center" justify="flex-end" spacing={2} width="full">
        <Box flex={1}>
          <KYDInput
            {...form.register("email")}
            label="Email addresses"
            placeholder="Add email addresses"
            error={form.formState.errors.email?.message}
            paddingRight={10}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onAddTestEmail(form.watch("email"));
              }
            }}
          />
        </Box>
        {form.watch("email") && (
          <IconButton
            aria-label="Add test email"
            icon={<FiPlus fontSize={16} />}
            sx={{
              height: 8,
              width: 8,
              zIndex: 2,
            }}
            size="xs"
            variant="ghost"
            onClick={() => onAddTestEmail(form.watch("email"))}
          />
        )}
      </HStack>
      <Description description={"You can add up to 10 emails"} />
      {selectedTestEmails.length > 0 && (
        <Box border="1px solid #E2E8F0" borderRadius="8px" p={3} mb={3}>
          {selectedTestEmails?.map((email) => (
            <HStack key={email} justifyContent="space-between">
              <Text fontSize="12px">{email}</Text>
              <IconButton
                aria-label="Remove email"
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                onClick={() => onChangeTestEmails(email)}
              />
            </HStack>
          ))}
        </Box>
      )}
      <Text
        fontWeight="600"
        fontSize="12px"
        color="#00000099"
        fontFamily="Helvetica"
      >
        Simulating contact
      </Text>
      {selectedContact ? (
        <HStack justifyContent="space-between">
          <Text fontSize="13px">{selectedContact.email}</Text>
          <IconButton
            aria-label="Remove email"
            icon={<CloseIcon />}
            size="xs"
            variant="ghost"
            onClick={() => setSelectedContact(null)}
          />
        </HStack>
      ) : (
        <KYDSearchInput
          items={items.map((item) => ({
            id: item.id,
            email: item.email,
          }))}
          onChange={refine}
          onSelect={setSelectedContact}
          placeholder="Search for a contact"
          searchField="email"
          displayField="email"
        />
      )}

      <Description
        description={
          "This will send the test email as if it's sent to the chosen subscriber. " +
          "Great for testing personalization or referral programs."
        }
      />
      <KYDButton
        backgroundColor="#2193F3"
        borderRadius="full"
        isDisabled={!canSendTestEmail}
        onClick={onSendTestEmail}
        height={"44px"}
        mb={4}
        mt={2}
        isLoading={isLoading}
        width={"fit-content"}
      >
        Send Test Email
      </KYDButton>
    </Stack>
  );
};

const SendTestEmail = (props: SendTestEmailProps) => {
  const [algoliaConfig, setAlgoliaConfig] = useState({
    searchClient: null,
    indexName: null,
  });

  const fetchContactsAlgoliaConfig = async () => {
    try {
      const response = await actions.ADMIN.fetchAlgoliaConfig({
        indexType: AlgoliaIndexType.CONTACTS,
      });
      const search = algoliasearch(
        response.events_config.app_id,
        response.events_config.api_key
      );
      setAlgoliaConfig({
        searchClient: search,
        indexName: response.events_config.index_name,
      });
    } catch (error) {
      console.error("Failed to fetch Algolia config:", error);
    }
  };
  useEffect(() => {
    fetchContactsAlgoliaConfig();
  }, []);

  if (!algoliaConfig.searchClient || !algoliaConfig.indexName) {
    return <Spinner />;
  }
  return (
    <InstantSearch
      indexName={algoliaConfig.indexName}
      searchClient={algoliaConfig.searchClient}
      initialUiState={{
        [algoliaConfig.indexName]: {
          menu: {
            status: "OPEN",
          },
        },
      }}
    >
      <SendTestEmailComponent {...props} />
    </InstantSearch>
  );
};

export default SendTestEmail;
